// S-I18N-W1b — bilingual <head> for the five scoped guest/landing routes.
// Mirrors the dependency-free DOM-upsert pattern from `market/use-course-head.ts`
// (no react-helmet, hard rule 5): idempotent tag upsert, SSR-safe, restores the
// static index.html defaults on unmount.
//
// `title`/`description` are already locale-resolved strings from the caller's
// `t()` — this hook only owns document.title/meta/canonical/hreflang, not copy.

import * as React from "react";

import { BRAND, brandNameForLocale } from "../brand/brand.config";
import { useLocale } from "../i18n/LocaleProvider";
import {
  PLATFORM_DEFAULT_LOCALE,
  localeMetadata,
  normalisePlatformLocale,
} from "../i18n/locale-registry";
import { SUPPORTED_LOCALES } from "../i18n/locale-state";

const MARK = "data-landing-seo";
const DESC_MAX = 160;

const origin = (): string => {
  if (typeof window !== "undefined" && window.location?.origin) return window.location.origin;
  return BRAND.siteUrl;
};

const clampDesc = (s: string | null | undefined): string => (s || "").replace(/\s+/g, " ").trim().slice(0, DESC_MAX);

/** Open Graph wants language_TERRITORY, while the registry stores canonical BCP47. */
export const openGraphLocaleFor = (raw: unknown): string => {
  const tag = normalisePlatformLocale(raw) ?? PLATFORM_DEFAULT_LOCALE;
  const canonical = localeMetadata(tag)?.locale ?? tag;
  try {
    const likely = new Intl.Locale(canonical).maximize();
    return likely.region ? `${likely.language}_${likely.region}` : likely.language;
  } catch {
    return canonical.replace(/-/g, "_");
  }
};

const upsertMeta = (attr: "name" | "property", key: string, content: string): void => {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute(MARK, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href: string): void => {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    el.setAttribute(MARK, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/** One `<link rel=alternate hreflang=… href=…>` per supported tag + x-default. */
const upsertHreflang = (hreflang: string, href: string): void => {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    el.setAttribute(MARK, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Apply bilingual title/meta-description/OG/hreflang for a scoped landing
 * route, restoring the static defaults on unmount. `path` is the route's
 * pathname (e.g. "/", "/about") — both locales serve the same URL (no
 * `/ar/…` prefix, ADR-A3), so hreflang alternates self-reference it.
 */
export const useLandingHead = (title: string, description: string, path: string): void => {
  const { locale } = useLocale();

  React.useEffect(() => {
    if (typeof document === "undefined" || !title) return;

    const url = `${origin()}${path}`;
    const brandName = brandNameForLocale(locale);
    const fullTitle = `${title} — ${brandName}`;
    const desc = clampDesc(description) || brandName;

    const prevTitle = document.title;
    const descEl = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevDesc = descEl?.getAttribute("content") ?? null;
    const canonEl = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    const hadCanon = !!canonEl;
    const prevCanon = canonEl?.getAttribute("href") ?? null;

    document.documentElement.lang = locale;
    document.title = fullTitle;
    upsertMeta("name", "description", desc);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:site_name", brandName);
    upsertMeta("property", "og:locale", openGraphLocaleFor(locale));
    upsertCanonical(url);
    SUPPORTED_LOCALES.forEach((tag) => upsertHreflang(tag, url));
    upsertHreflang("x-default", url);

    return () => {
      document.title = prevTitle || brandName;
      if (descEl && prevDesc != null) descEl.setAttribute("content", prevDesc);
      const ourCanon = document.head.querySelector<HTMLLinkElement>(`link[rel="canonical"][${MARK}="1"]`);
      if (ourCanon) {
        if (hadCanon && prevCanon != null) ourCanon.setAttribute("href", prevCanon);
        else ourCanon.remove();
      }
      document.head.querySelectorAll(`[${MARK}="1"]`).forEach((el) => {
        if (el.tagName === "META" && el.getAttribute("name") === "description") return;
        el.remove();
      });
    };
    // `locale` comes from `useLocale()` (React state), not the bare
    // `getLocale()` cell — LocaleProvider.change() moves i18next's strings
    // and that cell on separate ticks, so a bare read here could fire this
    // effect (re-triggered by the now-translated `title`) before the cell
    // catches up, baking the *previous* locale's brand name into the title.
  }, [title, description, path, locale]);
};
